import type { ComponentType, ReactNode } from "react";
import type { Role } from "./context";

/**
 * Contrato de UI de um módulo. Cada módulo exporta um `ModuleUi` em `modules/<nome>/ui`
 * (pacote `@vixio/<nome>-ui`); o `apps/os` descobre os pacotes pelo registro gerado e só
 * mostra o que está instalado no banco (`core.installed_modules()`) e ligado na organização
 * (`core.feature_flags`). Ver docs/UI_MODULOS.md.
 */

/** Os 10 domínios do descritivo (§2.2), na ordem do menu (§8.2). `platform` aparece como "Configurações". */
export type DomainKey =
  | "relationship"
  | "commercial"
  | "diagnostics"
  | "delivery"
  | "documents"
  | "money"
  | "people"
  | "content"
  | "governance"
  | "platform";

export const DOMAINS: ReadonlyArray<{ key: DomainKey; menu: string; title: string }> = [
  { key: "relationship", menu: "Relacionamento", title: "Atendimento e relacionamento" },
  { key: "commercial", menu: "Comercial", title: "Comercial" },
  { key: "diagnostics", menu: "Diagnóstico", title: "Diagnóstico e pesquisa" },
  { key: "delivery", menu: "Entrega", title: "Entrega" },
  { key: "documents", menu: "Documentos", title: "Documentos" },
  { key: "money", menu: "Dinheiro", title: "Dinheiro" },
  { key: "people", menu: "Pessoas", title: "Pessoas" },
  { key: "content", menu: "Conteúdo", title: "Conteúdo e autoridade" },
  { key: "governance", menu: "Gestão", title: "Gestão e governança" },
  { key: "platform", menu: "Configurações", title: "Núcleo e plataforma" },
];

export type IconType = ComponentType<{ "aria-hidden"?: boolean | "true" | "false"; className?: string }>;

export interface ModuleMenuItem {
  /** Caminho absoluto no app, ex.: "/comercial/pipeline". */
  path: string;
  label: string;
  /** Papel mínimo na organização para ver o item (e a rota). */
  minRole: Role;
  icon?: IconType;
  /** Domínio do item quando difere do domínio do módulo (ex.: core coloca "Pessoas" em Relacionamento). */
  domain?: DomainKey;
  /** Ordem dentro do domínio (menor primeiro). */
  order?: number;
  keywords?: string;
  end?: boolean;
  /** Exige administrador da plataforma (além do papel). */
  platformAdmin?: boolean;
}

export interface ModuleRoute {
  /** Caminho absoluto, pode ter parâmetros: "/comercial/propostas/:id". */
  path: string;
  element: ReactNode;
  minRole: Role;
}

export interface ModuleWidget {
  key: string;
  title: string;
  minRole: Role;
  /** Largura no grid do Início: 1 (um terço) a 3 (linha inteira). */
  span?: 1 | 2 | 3;
  order?: number;
  element: ComponentType;
}

export interface PublicRoute {
  /** Rota fora do login, ex.: "/p/:token". */
  path: string;
  element: ReactNode;
}

export interface ModuleUi {
  /** Igual ao `name` do module.json (e à feature flag, quando houver). */
  key: string;
  title: string;
  domain: DomainKey;
  icon: IconType;
  menu: ModuleMenuItem[];
  routes: ModuleRoute[];
  widgets?: ModuleWidget[];
  publicRoutes?: PublicRoute[];
  /** Portal do cliente (/portal): itens e rotas visíveis ao papel client. */
  portal?: {
    menu: Array<Omit<ModuleMenuItem, "domain">>;
    routes: ModuleRoute[];
    /** Painel do início do portal (ex.: pendências do cliente). */
    home?: ComponentType;
    /** Ordem do painel no início do portal (menor primeiro; padrão 50). */
    homeOrder?: number;
    /** Envolve todo o conteúdo do portal (ex.: exigir aceite dos termos antes de usar). */
    gate?: ComponentType<{ children: ReactNode }>;
    /** Controle na barra superior do portal (ex.: o sino de avisos do módulo portal). */
    topbar?: ComponentType;
  };
  /**
   * Controle que o módulo põe na barra superior do painel, em todas as telas (ex.: o botão de ajuda
   * contextual do módulo help). Some junto com o módulo quando ele é desligado ou não está instalado.
   */
  topbar?: ComponentType;
}

export function defineModuleUi(m: ModuleUi): ModuleUi {
  return m;
}
